import {Injectable} from '@angular/core';
import {ILayout} from '../models/label-interfaces';

export interface IShelfTalkerLayoutClipboard {
  layouts: ILayout[];
  sourceStoreId: string;
}

/**
 * In-memory clipboard for copying shelf talker layouts between stores (FE-4).
 * Lives for the browser session only; not persisted.
 */
@Injectable({
  providedIn: 'root',
})
export class ShelfTalkerLayoutClipboardService {
  private clipboard: IShelfTalkerLayoutClipboard | null = null;

  get count(): number {
    return this.clipboard?.layouts.length ?? 0;
  }

  /**
   * Stores deep copies of the given layouts for later paste.
   */
  setLayouts(layouts: ILayout[], sourceStoreId: string): void {
    this.clipboard = {
      sourceStoreId,
      layouts: layouts.map((layout: ILayout) => this.cloneLayout(layout)),
    };
  }

  /**
   * Returns a deep copy of all clipboard layouts, or an empty array.
   */
  getLayouts(): ILayout[] {
    if (!this.clipboard) {
      return [];
    }
    return this.clipboard.layouts.map((layout: ILayout) => this.cloneLayout(layout));
  }

  /**
   * Returns the most recently copied layout (last in the list), or null.
   */
  getLastLayout(): ILayout | null {
    if (!this.clipboard || this.clipboard.layouts.length === 0) {
      return null;
    }
    const last = this.clipboard.layouts[this.clipboard.layouts.length - 1];
    return this.cloneLayout(last);
  }

  clear(): void {
    this.clipboard = null;
  }

  private cloneLayout(layout: ILayout): ILayout {
    return structuredClone(layout);
  }
}
